import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Loader2, Plus, X, Wand2, Sparkles, Upload, Camera, PenLine,
  Info, Trash2, ImageIcon, ShieldAlert, Download, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_ROOMS = [
  { name: 'Living Room', width: '', length: '' },
  { name: 'Kitchen', width: '', length: '' },
  { name: 'Bedroom 1', width: '', length: '' },
  { name: 'Bathroom', width: '', length: '' },
];

const ROOM_PRESETS = [
  'Living Room', 'Kitchen', 'Dining Room', 'Bedroom', 'Bathroom', 'En-suite',
  'Hallway', 'Utility Room', 'Study', 'Garage', 'Conservatory', 'WC',
];

const FALLBACK_FLOORS = ['Basement', 'Ground Floor', '1st Floor', '2nd Floor', 'Loft'];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const AIFloorPlanGenerator = ({
  propertyType, bedrooms, bathrooms, areaSqft, country = 'UK',
  initialFloors = [], onFloorPlansChange, onFloorPlanGenerated,
}) => {
  // Wizard state
  const [enabled, setEnabled] = useState(initialFloors.length > 0);
  const [mode, setMode] = useState('upload'); // 'upload' | 'text'

  // Country-specific floor labels + disclaimer
  const [floorLabels, setFloorLabels] = useState(FALLBACK_FLOORS);
  const [disclaimer, setDisclaimer] = useState('');

  // Per-floor plans: { floor_label, floor_order, original_url, enhanced_url, show_original_to_buyers, notes }
  const [floors, setFloors] = useState(initialFloors);
  const [activeFloorIdx, setActiveFloorIdx] = useState(0);

  // Upload-mode state (per active floor)
  const [uploadPreview, setUploadPreview] = useState(null); // data URL
  const [uploadNotes, setUploadNotes] = useState('');
  const [enhancing, setEnhancing] = useState(false);

  // Text-mode state (legacy flow retained)
  const [rooms, setRooms] = useState(DEFAULT_ROOMS);
  const [genLoading, setGenLoading] = useState(false);

  // Fetch country-specific floor labels + disclaimer
  useEffect(() => {
    if (!enabled) return;
    axios.get(`${API_URL}/api/ai/floor-labels`, { params: { country } })
      .then(r => {
        setFloorLabels(r.data.labels || FALLBACK_FLOORS);
        setDisclaimer(r.data.disclaimer || '');
      })
      .catch(() => { setFloorLabels(FALLBACK_FLOORS); });
  }, [country, enabled]);

  // Bubble floor-plan state upward to parent form
  const syncUp = useCallback((next) => {
    onFloorPlansChange?.(next);
    // Legacy: also push enhanced urls into floor_plan_images for backward compat
    if (onFloorPlanGenerated) {
      next.forEach(f => { if (f.enhanced_url) onFloorPlanGenerated(f.enhanced_url, { skipDuplicate: true }); });
    }
  }, [onFloorPlansChange, onFloorPlanGenerated]);

  const addFloor = (label) => {
    const next = [...floors, {
      floor_label: label,
      floor_order: floors.length,
      original_url: null,
      enhanced_url: null,
      show_original_to_buyers: false,
      notes: '',
    }];
    setFloors(next);
    setActiveFloorIdx(next.length - 1);
    setUploadPreview(null);
    setUploadNotes('');
    syncUp(next);
  };

  const removeFloor = (idx) => {
    const next = floors.filter((_, i) => i !== idx).map((f, i) => ({ ...f, floor_order: i }));
    setFloors(next);
    setActiveFloorIdx(Math.max(0, Math.min(activeFloorIdx, next.length - 1)));
    syncUp(next);
  };

  const updateFloor = (idx, patch) => {
    const next = floors.map((f, i) => i === idx ? { ...f, ...patch } : f);
    setFloors(next);
    syncUp(next);
  };

  const handleSketchSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large — keep under 8MB');
      return;
    }
    const dataUrl = await fileToBase64(file);
    setUploadPreview(dataUrl);
  };

  const enhanceSketch = async () => {
    if (!uploadPreview) {
      toast.error('Upload a sketch first');
      return;
    }
    if (floors.length === 0) {
      toast.error('Add a floor first');
      return;
    }
    const active = floors[activeFloorIdx];
    setEnhancing(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/enhance-floor-plan`, {
        image_base64: uploadPreview,
        floor_label: active.floor_label,
        country,
        notes: uploadNotes,
      });
      const { original_url, enhanced_url, enhanced_base64 } = res.data;
      updateFloor(activeFloorIdx, {
        original_url: original_url || uploadPreview,
        enhanced_url: enhanced_url || (enhanced_base64 ? `data:image/png;base64,${enhanced_base64}` : null),
        notes: uploadNotes,
      });
      setUploadPreview(null);
      setUploadNotes('');
      toast.success(`${active.floor_label} redrawn in architect style`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI enhancement failed');
    } finally {
      setEnhancing(false);
    }
  };

  // Legacy text-mode: generate from room list
  const addRoom = (preset) => setRooms(prev => [...prev, { name: preset || 'New Room', width: '', length: '' }]);
  const updateRoom = (idx, field, value) => setRooms(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const removeRoom = (idx) => setRooms(prev => prev.filter((_, i) => i !== idx));

  // Memoise the available room presets to avoid recomputing on every render
  const availableRoomPresets = useMemo(
    () => ROOM_PRESETS.filter(p => !rooms.some(r => r.name === p)).slice(0, 6),
    [rooms]
  );

  const generateFromText = async () => {
    if (floors.length === 0) { toast.error('Add a floor first'); return; }
    const active = floors[activeFloorIdx];
    setGenLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-floor-plan`, {
        property_type: propertyType || 'house',
        bedrooms: parseInt(bedrooms) || 2,
        bathrooms: parseInt(bathrooms) || 1,
        area_sqft: parseFloat(areaSqft) || 1000,
        rooms: rooms.filter(r => r.name && r.width && r.length),
      });
      const url = res.data.image_url;
      const b64 = res.data.image_base64;
      const enhanced = url || (b64 ? `data:image/png;base64,${b64}` : null);
      if (!enhanced) throw new Error('No image returned');
      updateFloor(activeFloorIdx, { enhanced_url: enhanced, original_url: null });
      toast.success(`${active.floor_label} generated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenLoading(false);
    }
  };

  // --- Render: OFF state (seller opted out) ---
  if (!enabled) {
    return (
      <div className="border border-dashed border-slate-200 p-5 bg-slate-50/50" data-testid="floorplan-studio-off">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#1A1A1A]">Do you want to include a floor plan?</p>
            <p className="text-xs text-slate-500 mt-1">Buyers rank floor plans as the #1 most useful feature. You can upload a rough sketch and our AI will redraw it in clean architect style, or generate one from room sizes.</p>
            <div className="flex gap-2 mt-3">
              <Button type="button" size="sm" onClick={() => { setEnabled(true); if (floors.length === 0) addFloor(country === 'IN' ? 'Ground Floor' : 'Ground Floor'); }}
                className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-9 text-xs" data-testid="enable-floorplan-btn">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Yes, add floor plan
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => { setEnabled(false); setFloors([]); syncUp([]); }}
                className="h-9 text-xs border-slate-200 text-slate-500" data-testid="skip-floorplan-btn">
                Skip for now
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const active = floors[activeFloorIdx];
  const availableLabels = floorLabels.filter(lbl => !floors.some(f => f.floor_label === lbl));

  return (
    <div className="space-y-4" data-testid="floorplan-studio">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#E07A3A]" />
          <p className="text-sm font-semibold text-[#1A1A1A]">Floor Plan Studio — {country}</p>
        </div>
        <button type="button" onClick={() => { setEnabled(false); setFloors([]); syncUp([]); }}
          className="text-xs text-slate-400 hover:text-red-500" data-testid="disable-floorplan-btn">
          Remove all floor plans
        </button>
      </div>

      {/* Floor tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
        {floors.map((f, idx) => (
          <button key={`${f.floor_label}-${idx}`} type="button" onClick={() => { setActiveFloorIdx(idx); setUploadPreview(null); setUploadNotes(''); }}
            className={`px-3 py-1.5 text-xs font-medium border transition-colors flex items-center gap-1.5 ${idx === activeFloorIdx ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#E07A3A]'}`}
            data-testid={`floor-tab-${idx}`}>
            {f.enhanced_url && <Check className="h-3 w-3" />}
            {f.floor_label}
            <span onClick={(e) => { e.stopPropagation(); removeFloor(idx); }}
              className="ml-1 opacity-50 hover:opacity-100 cursor-pointer">
              <X className="h-3 w-3" />
            </span>
          </button>
        ))}

        {availableLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {availableLabels.map(lbl => (
              <button key={lbl} type="button" onClick={() => addFloor(lbl)}
                className="px-2.5 py-1 text-xs border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A] transition-colors flex items-center gap-1"
                data-testid={`add-floor-${lbl.replace(/\s+/g, '-').toLowerCase()}`}>
                <Plus className="h-3 w-3" />{lbl}
              </button>
            ))}
          </div>
        )}
      </div>

      {floors.length === 0 && (
        <p className="text-xs text-slate-500 italic">Add a floor above to get started — floors auto-labelled for {country}.</p>
      )}

      {active && (
        <>
          {/* Mode switcher */}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setMode('upload')}
              className={`flex-1 px-3 py-2 text-xs font-medium border transition-colors flex items-center justify-center gap-1.5 ${mode === 'upload' ? 'bg-[#FEF3EC] border-[#E07A3A] text-[#E07A3A]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
              data-testid="mode-upload-btn">
              <Upload className="h-3.5 w-3.5" /> Upload sketch → AI redraw
            </button>
            <button type="button" onClick={() => setMode('text')}
              className={`flex-1 px-3 py-2 text-xs font-medium border transition-colors flex items-center justify-center gap-1.5 ${mode === 'text' ? 'bg-[#FEF3EC] border-[#E07A3A] text-[#E07A3A]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
              data-testid="mode-text-btn">
              <PenLine className="h-3.5 w-3.5" /> Generate from room list
            </button>
          </div>

          {/* ============ UPLOAD MODE ============ */}
          {mode === 'upload' && (
            <div className="grid md:grid-cols-[1fr_1.2fr] gap-4">
              {/* How-to guide */}
              <div className="bg-[#F8FAFC] border border-slate-200 p-4 text-xs space-y-2">
                <p className="font-semibold text-[#1A1A1A] text-sm mb-2">How to get the best result</p>
                <div className="flex gap-2 items-start"><Camera className="h-3.5 w-3.5 text-[#E07A3A] mt-0.5 flex-shrink-0" /><span className="text-slate-600">Photograph your sketch/architect plan from directly above — flat and well-lit.</span></div>
                <div className="flex gap-2 items-start"><PenLine className="h-3.5 w-3.5 text-[#E07A3A] mt-0.5 flex-shrink-0" /><span className="text-slate-600">Label each room clearly on your sketch (Bedroom 1, Kitchen, etc.).</span></div>
                <div className="flex gap-2 items-start"><Info className="h-3.5 w-3.5 text-[#E07A3A] mt-0.5 flex-shrink-0" /><span className="text-slate-600">Add approximate dimensions if known (e.g. 4m x 3m).</span></div>
                <div className="flex gap-2 items-start"><ImageIcon className="h-3.5 w-3.5 text-[#E07A3A] mt-0.5 flex-shrink-0" /><span className="text-slate-600">You can also upload a scan/PDF page of the architect's drawing.</span></div>
                <div className="flex gap-2 items-start"><Sparkles className="h-3.5 w-3.5 text-[#E07A3A] mt-0.5 flex-shrink-0" /><span className="text-slate-600">AI will preserve your layout — it won't invent new rooms.</span></div>
              </div>

              {/* Upload + enhance panel */}
              <div className="space-y-3">
                {!active.enhanced_url && !uploadPreview && (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 hover:border-[#E07A3A] p-6 cursor-pointer transition-colors min-h-[180px]"
                    data-testid="sketch-upload-zone">
                    <Upload className="h-6 w-6 text-[#E07A3A] mb-2" />
                    <span className="text-sm font-medium text-[#1A1A1A]">Upload rough sketch of {active.floor_label}</span>
                    <span className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP — up to 8MB</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleSketchSelect} data-testid="sketch-input" />
                  </label>
                )}

                {uploadPreview && !active.enhanced_url && (
                  <div className="space-y-2">
                    <div className="relative border border-slate-200 bg-white p-2">
                      <img src={uploadPreview} alt="Your sketch" className="w-full h-auto max-h-64 object-contain" data-testid="sketch-preview" />
                      <button type="button" onClick={() => setUploadPreview(null)}
                        className="absolute top-2 right-2 bg-white/90 p-1 hover:bg-red-50">
                        <X className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    </div>
                    <Input value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)}
                      placeholder="Optional: any instructions (e.g. 'kitchen has an island', 'stairs face north')"
                      className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-10 text-sm" data-testid="sketch-notes-input" />
                    <Button type="button" onClick={enhanceSketch} disabled={enhancing}
                      className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-11" data-testid="enhance-sketch-btn">
                      {enhancing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redrawing (~30s)...</> : <><Wand2 className="h-4 w-4 mr-2" /> AI Redraw in Architect Style</>}
                    </Button>
                  </div>
                )}

                {active.enhanced_url && (
                  <div className="space-y-2" data-testid="enhanced-result">
                    <div className="grid grid-cols-2 gap-2">
                      {active.original_url && (
                        <div className="border border-slate-200 bg-white p-2">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1 font-semibold">Your sketch</p>
                          <img src={active.original_url} alt="Original sketch" className="w-full h-auto max-h-40 object-contain" />
                        </div>
                      )}
                      <div className="border border-[#E07A3A]/40 bg-white p-2">
                        <p className="text-[10px] text-[#E07A3A] uppercase tracking-wider mb-1 font-semibold">AI architect version</p>
                        <img src={active.enhanced_url} alt="AI enhanced floor plan" className="w-full h-auto max-h-40 object-contain" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {active.original_url && (
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={active.show_original_to_buyers}
                            onChange={(e) => updateFloor(activeFloorIdx, { show_original_to_buyers: e.target.checked })}
                            data-testid="show-original-checkbox" />
                          Also show original sketch to buyers
                        </label>
                      )}
                      <div className="ml-auto flex gap-1">
                        <a href={active.enhanced_url} download={`${active.floor_label}-plan.png`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-[#E07A3A] hover:text-[#C4652E] font-medium flex items-center gap-1">
                          <Download className="h-3 w-3" /> Download
                        </a>
                        <button type="button" onClick={() => updateFloor(activeFloorIdx, { enhanced_url: null, original_url: null })}
                          className="text-xs text-slate-500 hover:text-red-500 font-medium flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Redo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ TEXT MODE (legacy) ============ */}
          {mode === 'text' && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Define rooms and dimensions — AI will generate a floor plan for <b>{active.floor_label}</b>.</p>
              <div className="space-y-2">
                {rooms.map((room, idx) => (
                  <div key={`${room.name}-${idx}`} className="flex items-center gap-2" data-testid={`room-row-${idx}`}>
                    <Input value={room.name} onChange={(e) => updateRoom(idx, 'name', e.target.value)}
                      placeholder="Room name" className="flex-1 bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-10 text-sm" />
                    <div className="flex items-center gap-1">
                      <Input type="number" value={room.width} onChange={(e) => updateRoom(idx, 'width', e.target.value)}
                        placeholder="W" className="w-16 bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-10 text-sm text-center" />
                      <span className="text-xs text-slate-400">x</span>
                      <Input type="number" value={room.length} onChange={(e) => updateRoom(idx, 'length', e.target.value)}
                        placeholder="L" className="w-16 bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-10 text-sm text-center" />
                      <span className="text-xs text-slate-400">m</span>
                    </div>
                    <button type="button" onClick={() => removeRoom(idx)} className="text-slate-300 hover:text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableRoomPresets.map(preset => (
                  <button key={preset} type="button" onClick={() => addRoom(preset)}
                    className="px-2.5 py-1 text-xs border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A] transition-colors">
                    <Plus className="h-3 w-3 inline mr-1" />{preset}
                  </button>
                ))}
              </div>
              <Button type="button" onClick={generateFromText} disabled={genLoading || rooms.length === 0}
                className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-11" data-testid="generate-floorplan-btn">
                {genLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating (~30s)...</> : <><Wand2 className="h-4 w-4 mr-2" /> Generate {active.floor_label}</>}
              </Button>
              {active.enhanced_url && (
                <div className="border border-[#E07A3A]/40 bg-white p-2">
                  <p className="text-[10px] text-[#E07A3A] uppercase tracking-wider mb-1 font-semibold">{active.floor_label}</p>
                  <img src={active.enhanced_url} alt="Generated plan" className="w-full h-auto max-h-56 object-contain" />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Country-specific legal disclaimer */}
      {disclaimer && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 leading-relaxed" data-testid="floorplan-disclaimer">
          <ShieldAlert className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
          <span>{disclaimer}</span>
        </div>
      )}
    </div>
  );
};

export default AIFloorPlanGenerator;
